import { safeText } from '../lib/render';

/**
 * Render a single AI-validated reference as an APA-7 style entry.
 *
 * Expected `ref` shape (from reference_validator.validate_text):
 *   {
 *     status: 'valid' | 'corrected' | 'invalid',
 *     type: 'journal_article' | 'book' | 'report' | 'news_article' | …,
 *     authors: [ "Surname, F." , ... ],
 *     year: '2023',
 *     title: '...',
 *     journal: '...',  publisher: '...',
 *     volume: '...',   issue: '...',    pages: '...',
 *     doi: '...',      url: '...',
 *     replacement: { reason: '...' }     // when status !== 'valid'
 *   }
 */
export default function ApaReference({ ref }) {
  if (!ref || typeof ref !== 'object') return null;

  const authors = Array.isArray(ref.authors)
    ? ref.authors.filter(Boolean)
    : ref.authors
      ? [ref.authors]
      : [];
  let authorsStr;
  if (authors.length === 0) authorsStr = 'Anonymous';
  else if (authors.length === 1) authorsStr = safeText(authors[0]);
  else if (authors.length === 2) authorsStr = `${safeText(authors[0])}, & ${safeText(authors[1])}`;
  else {
    const head = authors.slice(0, -1).map(safeText).join(', ');
    authorsStr = `${head}, & ${safeText(authors[authors.length - 1])}`;
  }

  const year = safeText(ref.year, 'n.d.');
  const title = safeText(ref.title, '[no title]');
  const type = safeText(ref.type, '').toLowerCase();

  let sourceBlock = null;
  if (type === 'journal_article') {
    sourceBlock = (
      <em>
        {safeText(ref.journal)}
        {ref.volume ? <>, {safeText(ref.volume)}</> : null}
        {ref.issue ? <>({safeText(ref.issue)})</> : null}
      </em>
    );
  } else if (type === 'book' || type === 'book_chapter') {
    sourceBlock = safeText(ref.publisher);
  } else if (type === 'report' || type === 'working_paper') {
    sourceBlock = safeText(ref.publisher || ref.journal);
  } else if (type === 'news_article') {
    sourceBlock = <em>{safeText(ref.journal || ref.publisher)}</em>;
  } else if (type === 'thesis') {
    sourceBlock = <>[Doctoral dissertation, {safeText(ref.publisher)}]</>;
  }

  const status = safeText(ref.status, '').toLowerCase();
  const badge =
    status === 'invalid' ? { bg: '#fde8e8', fg: '#9b1c1c', label: 'unverified' } :
    status === 'corrected' ? { bg: '#fef3c7', fg: '#92400e', label: 'corrected' } :
    null;

  return (
    <li style={{ marginBottom: '0.65rem', wordBreak: 'break-word' }}>
      <span>{authorsStr} ({year}). {title}.</span>
      {sourceBlock && <> {sourceBlock}.</>}
      {ref.pages && <> {safeText(ref.pages)}.</>}
      {ref.doi && (
        <>
          {' '}
          <a
            href={`https://doi.org/${String(ref.doi).replace(/^\/+/, '')}`}
            target="_blank"
            rel="noreferrer"
            style={{ wordBreak: 'break-all' }}
          >
            https://doi.org/{safeText(ref.doi)}
          </a>
        </>
      )}
      {!ref.doi && ref.url && (
        <>
          {' '}
          <a
            href={safeText(ref.url)}
            target="_blank"
            rel="noreferrer"
            style={{ wordBreak: 'break-all' }}
          >
            {safeText(ref.url)}
          </a>
        </>
      )}
      {badge && (
        <span
          className="ms-2"
          style={{
            background: badge.bg,
            color: badge.fg,
            fontSize: '0.72rem',
            padding: '2px 7px',
            borderRadius: 10,
            fontWeight: 600,
            verticalAlign: 'middle',
          }}
          title={ref?.replacement?.reason || 'Flagged by the validator'}
        >
          {badge.label}
        </span>
      )}
      {ref?.replacement?.reason && status !== 'valid' && (
        <div className="small text-muted mt-1" style={{ fontStyle: 'italic' }}>
          {safeText(ref.replacement.reason)}
        </div>
      )}
    </li>
  );
}
